import { isValid as isValidIsoCountryCode } from "i18n-iso-countries";

import { CANADIAN_PROVINCE_CODES, validateZipCode } from "~/helpers/addressValidation";

export const isValidEmail = (email: string) => {
	return /^[^ ]+@[^. ][^ ]*\.[a-z]{2,}$/i.test(email);
};

export const isValidPhone = (phone: string) => {
	return /^\(\d\d\d\) \d\d\d-\d\d\d\d$/.test(phone);
};

export const isValidDate = (date: string) => {
	return /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/20\d\d$/.test(date);
};

export const formatPhone = (phone: string) => {
	const phoneParts = phone.match(/[^\d]*(\d{3})[^\d]*(\d{3})[^\d]*(\d{4})[^\d]*/);
	if (phoneParts && phoneParts.length === 4) {
		return "(" + phoneParts[1] + ") " + phoneParts[2] + "-" + phoneParts[3];
	} else {
		return "";
	}
};

// Server contract is strict E.164 (ValidE164PhoneNumber); the PWA's display mask is US 10-digit only, so this assumes US/Canada absent an explicit country code.
export const toE164Phone = (phone: string): string | null => {
	const trimmed = phone.trim();
	if (!trimmed) return null;

	const digits = trimmed.replace(/\D/g, "");
	if (trimmed.startsWith("+")) return digits.length >= 8 ? `+${digits}` : null;
	if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
	if (digits.length === 10) return `+1${digits}`;
	return null;
};

export const formatE164Phone = (e164: string): string => {
	const digits = e164.replace(/\D/g, "");
	const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
	if (national.length !== 10) return e164;
	return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
};

export const isValidCurrency = (cur: string) => {
	return /^(USD|CAD|MXN)$/.test(cur);
};

export const isValidDomain = (domain: string) => {
	return /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/i.test(domain.trim());
};

export const isValidCountry = (country: string) => {
	const code = country.trim().toUpperCase();
	return /^[A-Z]{2}$/.test(code) && isValidIsoCountryCode(code);
};

export const isValidZipcode = (zipcode: string) => {
	//##### or #####-####
	return /^\d{5}(-\d{4})?$/.test(zipcode);
};

export const isValidPostalCode = (postal: string, country: string) => validateZipCode(postal, country) === null;

export const inferCountryFromProvince = (province: string) => {
	return CANADIAN_PROVINCE_CODES.includes(province.trim().toUpperCase()) ? "CA" : "US";
};

export const isValidUpc = (upc: string) => {
	// Exactly 12 digits - numbers only, no letters or special characters
	return /^\d{12}$/.test(upc);
};

export const isValidGln = (gln: string) => {
	// Exactly 13 digits - numbers only, no letters or special characters
	return /^\d{13}$/.test(gln);
};

export const isValidOrganicLotCode = (lotCode: string) => {
	// Must stay aligned with the server-side ValidOrganicLotCode regex.
	return /^[0-9a-zA-Z_-]{1,64}$/.test(lotCode);
};

export const isValidCode = (code: string) => {
	// Must stay aligned with the server-side ValidCode CodeRegex; blank is treated as no code.
	const trimmed = code.trim();
	return trimmed === "" || /^[-a-zA-Z0-9./"%#& ]{1,32}$/.test(trimmed);
};

// Int32.MaxValue — the upper bound of the server's Int32 scalars.
export const INT32_MAX = 2147483647;

// Int32.MinValue, the lower bound of the server's Int32 scalars.
export const INT32_MIN = -2147483648;

export const VOLUME_POOL_CODE_MAX_LENGTH = 32;

// Must stay aligned with the server-side ValidReason MaxLength.
export const VALID_REASON_MAX_LENGTH = 1024;

export const PERCENTAGE_MAX = 100;

export const isValidVolumePoolCode = (code: string) => {
	// Must stay aligned with the server-side ValidVolumePoolCode regex: 1–32 letters, digits, spaces, underscores or hyphens.
	return /^[A-Za-z0-9 _-]{1,32}$/.test(code.trim());
};

export const hasValidCodeCharacters = (code: string) => {
	// Must stay aligned with the server-side ValidCode CodeRegex allowlist.
	return /^[a-zA-Z0-9 ~!@#$%^&*()\-_=+[\]{}:,.?/"]*$/.test(code);
};

export const isValidStringLength = (value: string, maxLength: number, minLength: number = 1) => {
	return value.length >= minLength && value.length <= maxLength;
};

export const isPositiveNumber = (value: null | number): boolean => value != null && value > 0;

export const requiredPositiveNumberError = (value: null | number, saveAttempted: boolean): string => {
	if (value != null && value <= 0) return "Must be greater than 0";
	if (saveAttempted && value == null) return "This field is required";
	return "";
};

export const isValidOrderType = (orderType: string, flowType: "BPF" | "MOIF") => {
	// Valid order types based on flow/tenant type
	const validTypes = {
		BPF: ["P", "C"], // P (PO), C (Credit)
		MOIF: ["P", "C", "R", "M"]
	};

	return validTypes[flowType].includes(orderType);
};
